/**
 * Chamber of Trial: pure, immutable update helpers over the parsed
 * `TrialData` object (see `trial-schema.ts`). Used by the session-3 form
 * view — the JSON editor never calls these, it edits text directly.
 *
 * Every helper spreads the object it touches and only overrides the field
 * being edited. This is deliberate: it is what keeps round-trip fidelity —
 * a field the forms do not render (`onWaveSpawned`, `onWaveCleared`,
 * `recipe`, or anything else) must survive an edit made three levels away
 * unchanged, by reference. Do not rewrite any of these to reconstruct an
 * object field-by-field from a fixed list of known keys — that is exactly
 * the bug this module exists to avoid.
 */

import type {
  TrialData,
  TrialStage,
  TrialWave,
  SpawnData,
  TrialChallenge,
  TrialReward,
  MonolithValues,
  StageModifier,
} from "@/lib/trial-schema";

// ─── Defaults for new rows ──────────────────────────────────────────────
// These are only used when an author explicitly adds a new floor/wave/spawn/
// challenge/reward — never applied to existing data.

export function makeDefaultSpawn(): SpawnData {
  return { _enemyIndex: 0, _spawnPos: -1, _forceLevel: false, _level: 1 };
}

export function makeDefaultWave(): TrialWave {
  return { spawnData: [makeDefaultSpawn()], onWaveSpawned: {}, onWaveCleared: {} };
}

export function makeDefaultChallenge(challengeType: number): TrialChallenge {
  return { challengeType, amount: 0 };
}

export function makeDefaultReward(): TrialReward {
  return { rewardType: 1, hero: 0, developerBuffID: "", itemID: "", recipe: null, amount: 0 };
}

export function makeDefaultStage(floor: number): TrialStage {
  return {
    floor,
    defenseSystem: 0,
    monolithValues: { hitInvincibleState: false, activationTime: 0, attackPotency: 0, effectDuration: 0 },
    modifier: { expMultiplier: 1, goldMultiplier: 1, damageMultiplier: 1, hpMultiplier: 1 },
    waveData: [makeDefaultWave()],
    challenges: [makeDefaultChallenge(0), makeDefaultChallenge(1), makeDefaultChallenge(2)],
    reward: [makeDefaultReward(), makeDefaultReward(), makeDefaultReward()],
  };
}

export function nextFloorNumber(data: TrialData): number {
  return data.stages.reduce((max, s) => Math.max(max, s.floor), 0) + 1;
}

// ─── Generic array helpers ──────────────────────────────────────────────

function replaceAt<T>(arr: T[], i: number, v: T): T[] {
  return arr.map((x, j) => (j === i ? v : x));
}
function removeAt<T>(arr: T[], i: number): T[] {
  return arr.filter((_, j) => j !== i);
}

// ─── TrialData-level: floors (append/remove-last only, per plan) ────────

export function addFloor(data: TrialData): TrialData {
  return { ...data, stages: [...data.stages, makeDefaultStage(nextFloorNumber(data))] };
}

export function removeLastFloor(data: TrialData): TrialData {
  if (data.stages.length === 0) return data;
  return { ...data, stages: data.stages.slice(0, -1) };
}

export function updateStageAt(data: TrialData, idx: number, updater: (s: TrialStage) => TrialStage): TrialData {
  return { ...data, stages: replaceAt(data.stages, idx, updater(data.stages[idx])) };
}

// ─── Stage-level: defense system / modifiers ─────────────────────────────

export function updateDefenseSystem(stage: TrialStage, value: number): TrialStage {
  return { ...stage, defenseSystem: value };
}

export function updateMonolithValues(stage: TrialStage, patch: Partial<MonolithValues>): TrialStage {
  return { ...stage, monolithValues: { ...stage.monolithValues, ...patch } };
}

export function updateModifier(stage: TrialStage, patch: Partial<StageModifier>): TrialStage {
  return { ...stage, modifier: { ...stage.modifier, ...patch } };
}

// ─── Stage-level: waves / spawns ──────────────────────────────────────────

export function addWave(stage: TrialStage): TrialStage {
  return { ...stage, waveData: [...stage.waveData, makeDefaultWave()] };
}

export function removeWave(stage: TrialStage, waveIdx: number): TrialStage {
  return { ...stage, waveData: removeAt(stage.waveData, waveIdx) };
}

export function addSpawn(stage: TrialStage, waveIdx: number): TrialStage {
  const wave = stage.waveData[waveIdx];
  const newWave: TrialWave = { ...wave, spawnData: [...wave.spawnData, makeDefaultSpawn()] };
  return { ...stage, waveData: replaceAt(stage.waveData, waveIdx, newWave) };
}

export function removeSpawn(stage: TrialStage, waveIdx: number, spawnIdx: number): TrialStage {
  const wave = stage.waveData[waveIdx];
  const newWave: TrialWave = { ...wave, spawnData: removeAt(wave.spawnData, spawnIdx) };
  return { ...stage, waveData: replaceAt(stage.waveData, waveIdx, newWave) };
}

export function updateSpawn(
  stage: TrialStage,
  waveIdx: number,
  spawnIdx: number,
  patch: Partial<SpawnData>
): TrialStage {
  const wave = stage.waveData[waveIdx];
  const newSpawn: SpawnData = { ...wave.spawnData[spawnIdx], ...patch };
  const newWave: TrialWave = { ...wave, spawnData: replaceAt(wave.spawnData, spawnIdx, newSpawn) };
  return { ...stage, waveData: replaceAt(stage.waveData, waveIdx, newWave) };
}

// ─── Stage-level: challenges ──────────────────────────────────────────────

export function addChallenge(stage: TrialStage): TrialStage {
  if (stage.challenges.length >= 3) return stage;
  const used = new Set(stage.challenges.map((c) => c.challengeType));
  const nextType = [0, 1, 2, 3].find((t) => !used.has(t)) ?? 0;
  return { ...stage, challenges: [...stage.challenges, makeDefaultChallenge(nextType)] };
}

export function removeChallenge(stage: TrialStage, idx: number): TrialStage {
  return { ...stage, challenges: removeAt(stage.challenges, idx) };
}

export function updateChallenge(stage: TrialStage, idx: number, patch: Partial<TrialChallenge>): TrialStage {
  return { ...stage, challenges: replaceAt(stage.challenges, idx, { ...stage.challenges[idx], ...patch }) };
}

// ─── Stage-level: rewards ─────────────────────────────────────────────────

export function addReward(stage: TrialStage): TrialStage {
  return { ...stage, reward: [...stage.reward, makeDefaultReward()] };
}

export function removeReward(stage: TrialStage, idx: number): TrialStage {
  return { ...stage, reward: removeAt(stage.reward, idx) };
}

/**
 * Updates one reward row, patch applied verbatim.
 *
 * This deliberately does NOT clear `itemID` when the reward type is not Item
 * (0). `GameManager.GetReward` reads `itemID` only on the Item branch, so the
 * field is inert there — but inert is not the same as wrong: live floor 13
 * keeps `key_soul_selector` on a Gold reward as a note for what that gold
 * stands in for. Clearing it would silently destroy authored data on an
 * unrelated edit to the same row. The validator warns about the shape instead,
 * which is the right place for a judgement only the author can make.
 */
export function updateReward(stage: TrialStage, idx: number, patch: Partial<TrialReward>): TrialStage {
  const next: TrialReward = { ...stage.reward[idx], ...patch };
  return { ...stage, reward: replaceAt(stage.reward, idx, next) };
}

// ─── Misc ─────────────────────────────────────────────────────────────────

const ORDINAL_SUFFIXES = ["th", "st", "nd", "rd"];
export function ordinal(n: number): string {
  const v = n % 100;
  const suffix = ORDINAL_SUFFIXES[(v - 20) % 10] || ORDINAL_SUFFIXES[v] || ORDINAL_SUFFIXES[0];
  return `${n}${suffix}`;
}
